import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { FolderOpen, MapPin } from "lucide-react";
import { useMyWorkerRecord, useMyStats, useMyWorkOrders, useMyBills } from "@/hooks/useWorkerPortal";
import { useIsMobile } from "@/hooks/use-mobile";
import { format } from "date-fns";

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

const woStatusBadge = (status: string) => {
  switch (status) {
    case "draft":
      return <Badge variant="outline" className="text-[10px] bg-gray-100 text-gray-700">Draft</Badge>;
    case "open":
    case "active":
      return <Badge className="text-[10px] bg-green-100 text-green-800 border-green-300">Active</Badge>;
    case "approved":
      return <Badge className="text-[10px] bg-green-100 text-green-800 border-green-300">Approved</Badge>;
    case "closed":
      return <Badge className="text-[10px] bg-blue-100 text-blue-800 border-blue-300">Closed</Badge>;
    case "voided":
      return <Badge variant="destructive" className="text-[10px]">Voided</Badge>;
    default:
      return <Badge variant="outline" className="text-[10px] capitalize">{status}</Badge>;
  }
};

const billStatusBadge = (status: string) => {
  switch (status) {
    case "draft":
      return <Badge variant="outline" className="text-[10px] bg-gray-100 text-gray-700 border-gray-300">Draft</Badge>;
    case "pending":
      return <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-300">Pending</Badge>;
    case "approved":
      return <Badge className="text-[10px] bg-green-100 text-green-800 border-green-300">Approved</Badge>;
    case "paid":
      return <Badge className="text-[10px] bg-blue-100 text-blue-800 border-blue-300">Paid</Badge>;
    case "voided":
      return <Badge variant="destructive" className="text-[10px]">Voided</Badge>;
    default:
      return <Badge variant="outline" className="text-[10px]">{status}</Badge>;
  }
};

const WorkerProjects = () => {
  const { data: worker, isLoading: wl } = useMyWorkerRecord();
  const stats = useMyStats(worker?.id);
  const { data: workOrders = [], isLoading: woLoading } = useMyWorkOrders(worker?.vendor_id || undefined);
  const { data: bills = [], isLoading: blLoading } = useMyBills(worker?.vendor_id || undefined);
  const isMobile = useIsMobile();

  const project = worker?.personnel_project as any;
  const schedule = worker?.rate_schedule as any;

  const isLoading = wl || woLoading || blLoading;

  if (isLoading) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-40" /></div>;

  // Group bills by work_order_id
  const billsByWO = new Map<string, any[]>();
  for (const b of bills) {
    if (b.work_order_id) {
      if (!billsByWO.has(b.work_order_id)) billsByWO.set(b.work_order_id, []);
      billsByWO.get(b.work_order_id)!.push(b);
    }
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">My Projects</h1>

      {!project ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FolderOpen className="h-10 w-10 mx-auto mb-2 text-muted-foreground/30" />
            <p className="text-muted-foreground">No active project assignments yet.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{project.name}</CardTitle>
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 text-[10px]">
                {project.status || "active"}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Project #</p>
                <p className="font-medium">{project.project_number || "--"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Role</p>
                <p className="font-medium">{schedule?.role_name || "Worker"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Rate</p>
                <p className="font-medium">{fmt(schedule?.bill_rate_regular || 0)}/HR</p>
              </div>
              <div>
                <p className="text-muted-foreground">OT Rate</p>
                <p className="font-medium">{fmt(schedule?.bill_rate_ot || 0)}/HR</p>
              </div>
            </div>

            {(project.location_city || project.location_state) && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" />
                {[project.location_city, project.location_state].filter(Boolean).join(", ")}
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm pt-2 border-t">
              <div>
                <p className="text-muted-foreground">Start Date</p>
                <p className="font-medium">{worker?.start_date ? format(new Date(worker.start_date), "MMM d, yyyy") : "--"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">YTD Hours</p>
                <p className="font-medium">{stats.ytdHours.toFixed(1)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">YTD Earned</p>
                <p className="font-medium">{fmt(stats.ytdEarned)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Work Orders */}
      {workOrders.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Work Orders</CardTitle></CardHeader>
          <CardContent>
            {isMobile ? (
              <div className="space-y-3">
                {workOrders.map((wo: any) => {
                  const woBills = billsByWO.get(wo.id) || [];
                  const billedTotal = woBills
                    .filter((b: any) => b.status !== "voided")
                    .reduce((s: number, b: any) => s + (b.total || 0), 0);
                  const remaining = Math.max(0, (wo.amount || 0) - billedTotal);
                  const billedPct = wo.amount > 0 ? Math.min(100, (billedTotal / wo.amount) * 100) : 0;

                  return (
                    <div key={wo.id} className="border rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">WO-{wo.number}</span>
                        {woStatusBadge(wo.status)}
                      </div>
                      {wo.description && (
                        <p className="text-sm text-muted-foreground truncate">{wo.description}</p>
                      )}
                      <div className="text-sm">
                        <div className="flex justify-between mb-1">
                          <span className="text-muted-foreground">Value</span>
                          <span className="font-medium">{fmt(wo.amount || 0)}</span>
                        </div>
                        <Progress value={billedPct} className="h-1.5 mb-1" />
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Billed: {fmt(billedTotal)}</span>
                          <span>Remaining: {fmt(remaining)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>WO #</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Total Value</TableHead>
                    <TableHead className="text-right">Billed</TableHead>
                    <TableHead className="text-right">Remaining</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workOrders.map((wo: any) => {
                    const woBills = billsByWO.get(wo.id) || [];
                    const billedTotal = woBills
                      .filter((b: any) => b.status !== "voided")
                      .reduce((s: number, b: any) => s + (b.total || 0), 0);
                    const remaining = Math.max(0, (wo.amount || 0) - billedTotal);
                    const billedPct = wo.amount > 0 ? Math.min(100, (billedTotal / wo.amount) * 100) : 0;

                    return (
                      <TableRow key={wo.id}>
                        <TableCell className="font-medium">WO-{wo.number}</TableCell>
                        <TableCell className="text-sm max-w-[200px] truncate">
                          {wo.description || "--"}
                        </TableCell>
                        <TableCell>{woStatusBadge(wo.status)}</TableCell>
                        <TableCell className="text-right font-medium">{fmt(wo.amount || 0)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-col items-end gap-1">
                            <span className="text-sm">{fmt(billedTotal)}</span>
                            <Progress value={billedPct} className="h-1.5 w-16" />
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-medium">{fmt(remaining)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Bills by Work Order */}
      {workOrders.length > 0 && bills.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Bill Status by Work Order</CardTitle></CardHeader>
          <CardContent>
            {isMobile ? (
              <div className="space-y-3">
                {bills.map((b: any) => (
                  <div key={b.id} className="border rounded-lg p-3 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{b.bill_number}</span>
                      {billStatusBadge(b.status)}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {b.work_orders ? `WO-${b.work_orders.number}` : "--"}
                      </span>
                      <span className="text-muted-foreground">
                        {b.bill_date ? format(new Date(b.bill_date), "MMM d, yyyy") : "--"}
                      </span>
                    </div>
                    <p className="text-lg font-semibold">{fmt(b.total || 0)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bill #</TableHead>
                    <TableHead>Work Order</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bills.map((b: any) => (
                    <TableRow key={b.id}>
                      <TableCell className="font-medium">{b.bill_number}</TableCell>
                      <TableCell className="text-sm">
                        {b.work_orders ? `WO-${b.work_orders.number}` : "--"}
                      </TableCell>
                      <TableCell>{b.bill_date ? format(new Date(b.bill_date), "MMM d, yyyy") : "--"}</TableCell>
                      <TableCell className="text-right font-medium">{fmt(b.total || 0)}</TableCell>
                      <TableCell>{billStatusBadge(b.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WorkerProjects;
